import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { useWidgetRefresh } from '../../contexts/WidgetRefreshContext';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { GlassCard } from './GlassCard';
import { RefreshButton } from './RefreshButton';

type ErrorRetryProps = {
  message: string;
  onRetry?: () => void;
  refreshing?: boolean;
  /** Inline row inside a widget instead of a standalone card. */
  compact?: boolean;
};

/** Error copy plus an explicit Refresh action — never leave a failed widget dead. */
export function ErrorRetry({
  message,
  onRetry,
  refreshing,
  compact = false,
}: ErrorRetryProps) {
  const scoped = useWidgetRefresh();
  const handleRetry = onRetry ?? scoped?.refresh;
  const isRefreshing = refreshing ?? scoped?.refreshing ?? false;

  const body = (
    <View style={[styles.row, compact && styles.rowCompact]}>
      <Text style={styles.message}>{message}</Text>
      {handleRetry ? (
        <RefreshButton
          onPress={handleRetry}
          refreshing={isRefreshing}
          compact={compact}
          accessibilityLabel="Retry"
        />
      ) : null}
    </View>
  );

  if (compact) return body;

  return <GlassCard style={styles.card}>{body}</GlassCard>;
}

const styles = StyleSheet.create({
  card: {
    borderColor: colors.warning,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  rowCompact: {
    marginTop: spacing.xs,
  },
  message: {
    ...typography.body,
    color: colors.warning,
    flex: 1,
    flexShrink: 1,
  },
});
